import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import "./BillingPayment.scss";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SideNavbar from "./SideNavbar";
import NoDataPage from "./NoDataPage";
import InvoicePayment from "./InvoicePayment";
import DisplayDate from "../../components/date/DisplayDate";
import { formatDateForDisplay } from "../../utils/displayDateFormat";
import { toast } from "react-toastify";
import { displayPatientName } from "../../utils/displayPatientName";
import { usePermissions } from "../login/PermissionContext";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

/** Decode JWT payload (no signature verify — same pattern as client audit helpers). */
function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    if (pad) base64 += "=".repeat(4 - pad);
    const binary = atob(base64);
    const jsonPayload = decodeURIComponent(
      Array.from(binary, (c) => "%" + `00${c.charCodeAt(0).toString(16)}`.slice(-2)).join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const Refund = () => {
  const history = useHistory();
  const { userInfo, loading: permissionLoading } = usePermissions();
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const [authToken, setAuthToken] = useState("");
  const [facilityName, setFacilityName] = useState(
    sessionStorage.getItem("facility_name") || "",
  );

  const normalizeRole = (r) =>
    String(r || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  /** API role + session role (login often mirrors sessionStorage.roleType). */
  const roleFromApi = normalizeRole(userInfo?.role_permission?.[0]?.role);
  const roleFromSession = normalizeRole(sessionStorage.getItem("roleType"));
  const isFacilityAdmin =
    roleFromApi === "facility admin" || roleFromSession === "facility admin";

  const [refundList, setRefundList] = useState([]);
  const [refundListLoading, setRefundListLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [refundSearchText, setRefundSearchText] = useState("");
  const [debouncedRefundSearch, setDebouncedRefundSearch] = useState("");
  const [refundPagination, setRefundPagination] = useState({
    page: 1,
    limit: 10,
    total_records: 0,
    total_pages: 0,
  });

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [invoiceForPayment, setInvoiceForPayment] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveModalRow, setApproveModalRow] = useState(null);
  const [approveLineState, setApproveLineState] = useState([]);
  const [submittingApprove, setSubmittingApprove] = useState(false);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectModalRow, setRejectModalRow] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [submittingReject, setSubmittingReject] = useState(false);

  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [refundSearchQuery, setRefundSearchQuery] = useState("");
  const [debouncedRefundSearchQuery, setDebouncedRefundSearchQuery] = useState("");
  const [selectedRefundInvoiceId, setSelectedRefundInvoiceId] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const [refundRows, setRefundRows] = useState([{ itemIndex: "", qty: "", amount: "" }]);
  const [amountRefundAmount, setAmountRefundAmount] = useState("");
  const [allInvoicesForRefund, setAllInvoicesForRefund] = useState([]);
  const [refundLoading, setRefundLoading] = useState(false);
  const [submittingRefund, setSubmittingRefund] = useState(false);

  useEffect(() => {
    setAuthToken(sessionStorage.getItem("accessToken") ?? "");
    setFacilityName(sessionStorage.getItem("facility_name") || "");
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRefundSearch(refundSearchText.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [refundSearchText]);

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [debouncedRefundSearch]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedRefundSearchQuery(refundSearchQuery);
    }, 1500);
    return () => clearTimeout(debounceTimer);
  }, [refundSearchQuery]);

  const formatInr = (n) =>
    Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getInvoiceItems = (invoice) => {
    const items = invoice?.items || invoice?.itemsList || invoice?.invoice_items || [];
    return Array.isArray(items) ? items : [];
  };

  const getItemName = (item) =>
    item?.item_name || item?.service_name || item?.name || "Unnamed item";

  const getItemQty = (item) => Number(item?.quantity ?? item?.qty ?? 0) || 0;
  const getInvoiceItemId = (item) => {
    const raw = item?.invoice_item_id;
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!/^\d+$/.test(s)) return null;
    return Number(s);
  };
  const getRefundedApprovedQty = (item) =>
    Number(item?.refunded_approved_q ?? item?.refunded_qty ?? 0) || 0;
  const getRefundPendingQty = (item) =>
    Number(item?.refund_pending_qty ?? item?.pending_refund_qty ?? 0) || 0;
  const getRefundAvailableQty = (item) => {
    const explicit = Number(item?.refund_available_qty);
    if (Number.isFinite(explicit)) return Math.max(0, explicit);
    const sold = getItemQty(item);
    return Math.max(0, sold - getRefundedApprovedQty(item) - getRefundPendingQty(item));
  };
  const isItemRefundable = (item) => {
    if (typeof item?.is_refundable === "boolean") return item.is_refundable;
    return getRefundAvailableQty(item) > 0;
  };

  const getItemAmount = (item) =>
    Number(item?.subtotal ?? item?.amount ?? item?.gross ?? item?.item_price ?? 0) || 0;

  const getItemListPrice = (item) =>
    Number(item?.item_price ?? item?.price ?? item?.rate ?? 0) || 0;

  const getItemDiscountPercent = (item) =>
    Number(item?.discount_percentage ?? item?.discount_percent ?? item?.discount ?? 0) || 0;

  // Refund calculation should use charged unit amount (after discount), not list price.
  const getItemUnitPrice = (item) => {
    const billedQty = getItemQty(item);
    const lineAmount = getItemAmount(item);
    if (billedQty > 0 && lineAmount > 0) return lineAmount / billedQty;
    const explicitUnitPrice = getItemListPrice(item);
    if (explicitUnitPrice > 0) return explicitUnitPrice;
    return 0;
  };

  const getRefundApiHeaders = () => ({
    Authorization: `Bearer ${authToken}`,
    "Content-Type": "application/json",
    "App-Mode": sessionStorage.getItem("appMode") || "",
    "Org-Code": sessionStorage.getItem("orgCode") || "",
    Username: sessionStorage.getItem("username") || "",
    "User-Access-Key": sessionStorage.getItem("accessKey") || "",
  });

  /**
   * requested_by = Django auth User.id (numeric).
   * SimpleJWT access payload usually has claim `user_id`.
   */
  const getRequestedByUserId = () => {
    const token = sessionStorage.getItem("accessToken") || authToken;
    const payload = decodeJwtPayload(token);
    if (payload) {
      const raw = payload.user_id ?? payload.userId;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };

  const formatDisplayDate = (iso) => {
    if (!iso) return "—";
    return formatDateForDisplay(iso) || "—";
  };

  const fetchRefundList = useCallback(async () => {
    if (!authToken || !facilityName || !REACT_APP_BACKEND_ROUTE_URL) return;
    setRefundListLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const searchValue = debouncedRefundSearch.trim();
      const payload = {
        hip_id: facilityName,
        page,
        limit,
      };
      if (searchValue) {
        payload.search = searchValue;
      }
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}refund_list/`,
        payload,
        { headers: getRefundApiHeaders() },
      );
      if (res?.data?.status !== "Success") {
        toast.error(res?.data?.message || "Failed to load refunds.");
        setRefundList([]);
        return;
      }
      const rows = Array.isArray(res.data.refunds) ? res.data.refunds : [];
      const pg = res.data.pagination || {};
      setRefundList(
        rows.map((r) => ({
          ...r,
          id: r.refund_request_id,
        })),
      );
      setRefundPagination({
        page: Number(pg.page) || page,
        limit: Number(pg.limit) || limit,
        total_records: Number(pg.total_records) || 0,
        total_pages: Number(pg.total_pages) || 0,
      });
    } catch (e) {
      console.error("refund_list:", e);
      toast.error(e?.response?.data?.message || e?.message || "Failed to load refunds.");
      setRefundList([]);
    } finally {
      setRefundListLoading(false);
    }
  }, [
    authToken,
    facilityName,
    paginationModel.page,
    paginationModel.pageSize,
    debouncedRefundSearch,
    REACT_APP_BACKEND_ROUTE_URL,
  ]);

  useEffect(() => {
    if (!permissionLoading) fetchRefundList();
  }, [permissionLoading, fetchRefundList]);

  const mapApprovedRefundToPaymentInvoice = (row) => {
    const amount = Number(row.approved_amount || row.requested_amount || 0).toFixed(2);
    const lines = Array.isArray(row.items) ? row.items : [];
    return {
      refund_request_id: row.refund_request_id,
      refund_number: row.refund_number,
      settlement_status: row.settlement_status,
      invoice_id: row.invoice_db_id,
      invoice_number: row.invoice_number,
      patient_name: row.patient_name,
      patient_id: row.patient_record_id ?? row.patient_id,
      approved_by_id: row.approved_by_id,
      total_amount: amount,
      partial_payment: "0",
      // Carried into the post-payout receipt so it renders the amount-only layout.
      refund_mode: isAmountRefundRow(row) ? "AMOUNT" : "ITEM",
      items: lines.map((it) => ({
        item_name: it.item_name,
        quantity:
          Number(it.approved_quantity) > 0 ? it.approved_quantity : it.quantity,
        item_price: it.item_price,
        subtotal: it.approved_amount || it.subtotal,
      })),
    };
  };

  const handleOpenRefundPayment = (row) => {
    setInvoiceForPayment(mapApprovedRefundToPaymentInvoice(row));
    setOpenPaymentModal(true);
  };

  const handleOpenRefundReceipt = (row) => {
    const items = Array.isArray(row?.items) ? row.items : [];
    const paymentDetails = Array.isArray(row?.payment_details) ? row.payment_details : [];
    const settlementPayment = paymentDetails.find(
      (p) => String(p?.payment_type).toUpperCase() === "REFUND" || String(p?.status).toUpperCase() === "SUCCESS",
    ) || paymentDetails[0] || null;

    const resolvedMode =
      row?.mode_of_payment || row?.payment_mode ||
      row?.payout_mode || row?.settlement_mode ||
      settlementPayment?.mode_of_payment || settlementPayment?.payment_mode ||
      "N/A";
    const resolvedRef =
      row?.transaction_ref_no || row?.ref_no ||
      row?.payout_ref_no || row?.settlement_ref_no ||
      settlementPayment?.transaction_ref_no || settlementPayment?.ref_no ||
      "N/A";

    const receiptData = {
      invoice_number: row?.invoice_number || "",
      patient_name: row?.patient_name || "",
      patient_id: row?.patient_record_id ?? row?.patient_id,
      invoice_date: row?.invoice_date || row?.created_at || "",
      refund_number: row?.refund_number || row?.receipt_number || row?.refund_receipt_no || "N/A",
      receipt_no: row?.receipt_number || row?.refund_receipt_no || row?.refund_number || "N/A",
      approved_at: row?.approved_at || row?.approved_at_utc || row?.updated_at || row?.created_at,
      settlement_status: row?.settlement_status || "UNSETTLED",
      payment_mode: resolvedMode,
      ref_no: resolvedRef,
      amount_paid: row?.paid_amount || row?.approved_amount || row?.requested_amount || "0.00",
      refund_amount: row?.paid_amount || row?.approved_amount || row?.requested_amount || "0.00",
      total_amount: row?.approved_amount || row?.requested_amount || "0.00",
      payment_details: paymentDetails,
      refund_mode: row?.refund_mode || (
        items.length > 0 && items.every((it) => it?.item_id == null) ? "AMOUNT" : "ITEM"
      ),
      itemsList: items.map((it) => ({
        item_id: it?.item_id,
        item_name: it?.item_name || "—",
        approved_quantity: Number(it?.approved_quantity ?? it?.quantity ?? 0) || 0,
        quantity: Number(it?.approved_quantity ?? it?.quantity ?? 0) || 0,
        item_price: it?.item_price,
        approved_amount: it?.approved_amount,
        subtotal: it?.approved_amount || it?.subtotal || 0,
      })),
    };
    history.push(`/refundReceipt/${receiptData.invoice_number || row?.refund_request_id}`, {
      data: receiptData,
    });
  };

  const safeMoney = (v) => {
    const n = Number(String(v ?? "").replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  // For refund approval rows, always prefer charged unit amount.
  const getApprovalUnitPrice = (item) => {
    const charged = safeMoney(item?.charged_price);
    if (charged > 0) return charged;
    const qty = Number(item?.quantity ?? 0) || 0;
    const subtotal = safeMoney(item?.subtotal);
    if (qty > 0 && subtotal > 0) return subtotal / qty;
    return safeMoney(item?.item_price);
  };

  const resetApproveModal = () => {
    setApproveModalOpen(false);
    setApproveModalRow(null);
    setApproveLineState([]);
  };

  const closeApproveModal = () => {
    if (submittingApprove) return;
    resetApproveModal();
  };

  const openApproveModal = (row) => {
    if (rejectModalOpen && !submittingReject) resetRejectModal();
    const items = Array.isArray(row?.items) ? row.items : [];
    setApproveModalRow(row);
    setApproveLineState(
      items.map((it, idx) => ({
        key: String(it?.id ?? `idx-${idx}`),
        item: it,
        checked: true,
        requestedQuantity: Number(it?.quantity ?? 0) || 0,
        approvedQuantity: Number(it?.quantity ?? 0) || 0,
        unitPrice: getApprovalUnitPrice(it),
        // Amount refund (bill-discount invoice): one line, no qty, approver may lower the rupee amount.
        requestedAmount: safeMoney(it?.subtotal ?? it?.charged_price ?? it?.price),
        approvedAmount: safeMoney(it?.subtotal ?? it?.charged_price ?? it?.price).toFixed(2),
      })),
    );
    setApproveModalOpen(true);
  };

  const updateApproveAmount = (key, value) => {
    setApproveLineState((prev) =>
      prev.map((line) => {
        if (line.key !== key) return line;
        if (value === "") return { ...line, approvedAmount: "" };
        const n = Number(value);
        if (!Number.isFinite(n) || n < 0) return line;
        return { ...line, approvedAmount: value };
      }),
    );
  };

  const toggleApproveLine = (key) => {
    setApproveLineState((prev) =>
      prev.map((line) =>
        line.key === key ? { ...line, checked: !line.checked } : line,
      ),
    );
  };

  const updateApproveQuantity = (key, value) => {
    setApproveLineState((prev) =>
      prev.map((line) => {
        if (line.key !== key) return line;
        if (value === "") return { ...line, approvedQuantity: "" };
        const n = Number(value);
        if (!Number.isFinite(n)) return line;
        const maxQty = Number(line.requestedQuantity || 0);
        const clamped = Math.min(Math.max(Math.round(n), 0), maxQty);
        return { ...line, approvedQuantity: clamped };
      }),
    );
  };

  const isRefundRowActionBusy = (row) => {
    const id = row?.refund_request_id;
    if (id == null) return false;
    return (
      (submittingApprove && approvingId === id) ||
      (submittingReject && rejectingId === id)
    );
  };

  const resetRejectModal = () => {
    setRejectModalOpen(false);
    setRejectModalRow(null);
  };

  const closeRejectModal = () => {
    if (submittingReject) return;
    resetRejectModal();
  };

  const openRejectModal = (row) => {
    if (approveModalOpen && !submittingApprove) resetApproveModal();
    setRejectModalRow(row);
    setRejectModalOpen(true);
  };

  /** Backend path for reject/approve refund decision. */
  const submitRejectRefundFromModal = async () => {
    const row = rejectModalRow;
    const id = row?.refund_request_id;
    if (id == null || !REACT_APP_BACKEND_ROUTE_URL || !facilityName) return;
    const approvedBy = getRequestedByUserId();
    if (approvedBy == null) {
      toast.error("Could not read user id from access token. Please re-login and try again.");
      return;
    }

    setRejectingId(id);
    setSubmittingReject(true);
    try {
      const rowItems = Array.isArray(row?.items) ? row.items : [];
      const payload = {
        hip_id: facilityName,
        refund_request_id: id,
        status: "REJECTED",
        approved_by: approvedBy,
        approved_amount: "0.00",
        items: rowItems.map((it) => ({
          id: it?.id,
          status: "REJECTED",
          approved_quantity: 0,
          approved_amount: "0.00",
        })),
      };
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}refund_approval/`,
        payload,
        { headers: getRefundApiHeaders() },
      );
      if (res?.data?.status === "Success") {
        toast.success(res?.data?.message || "Refund request rejected.");
        resetRejectModal();
        fetchRefundList();
      } else {
        toast.error(res?.data?.message || "Reject failed.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Reject failed.",
      );
    } finally {
      setRejectingId(null);
      setSubmittingReject(false);
    }
  };

  /** Contract: POST refund_approval/ with line-wise decision payload. */
  const submitApproveRefundFromModal = async () => {
    const row = approveModalRow;
    const id = row?.refund_request_id;
    if (id == null || !REACT_APP_BACKEND_ROUTE_URL || !facilityName) return;

    const hasLineItems = approveLineState.length > 0;
    if (!hasLineItems) {
      toast.error("Approve requires line items from refund_list response.");
      return;
    }

    if (!approveLineState.some((l) => l.checked)) {
      toast.error("Select at least one item to approve.");
      return;
    }

    const linePayload = [];
    let approvedTotal = 0;

    if (isAmountRefundRow(row)) {
      for (const line of approveLineState) {
        if (!line.item?.id) {
          toast.error("Refund line is missing id.");
          return;
        }
        const requested = Number(line.requestedAmount || 0);
        const approved = Number(line.approvedAmount);
        if (!Number.isFinite(approved) || approved <= 0) {
          toast.error("Enter an approved amount greater than zero.");
          return;
        }
        if (approved > requested + 0.001) {
          toast.error(`Approved amount cannot exceed the requested amount (Rs ${requested.toFixed(2)}).`);
          return;
        }
        const exact = Number(approved.toFixed(2));
        approvedTotal = Number((approvedTotal + exact).toFixed(2));
        linePayload.push({
          id: line.item.id,
          status: "APPROVED",
          approved_quantity: 1,
          approved_amount: exact.toFixed(2),
        });
      }
    } else {
    for (const line of approveLineState) {
      const requestedQty = Number(line.requestedQuantity || 0);
      const unit = Number(line.unitPrice || 0);

      if (!line.item?.id) {
        toast.error("One or more refund items are missing id.");
        return;
      }

      if (!line.checked) {
        linePayload.push({
          id: line.item.id,
          status: "REJECTED",
          approved_quantity: 0,
          approved_amount: "0.00",
        });
        continue;
      }

      const approvedQuantity = Number(line.approvedQuantity || 0);
      if (requestedQty <= 0 || unit <= 0) {
        toast.error(`Invalid quantity/price for ${line.item?.item_name || "an item"}.`);
        return;
      }
      if (!Number.isFinite(approvedQuantity) || approvedQuantity <= 0) {
        toast.error(`Approved quantity is required for ${line.item?.item_name || "an item"}.`);
        return;
      }
      if (approvedQuantity > requestedQty) {
        toast.error(
          `Approved quantity for ${line.item?.item_name || "an item"} must be between 1 and ${requestedQty}.`,
        );
        return;
      }

      const exactLineAmount = Number((unit * approvedQuantity).toFixed(2));
      approvedTotal = Number((approvedTotal + exactLineAmount).toFixed(2));
      linePayload.push({
        id: line.item.id,
        status: "APPROVED",
        approved_quantity: approvedQuantity,
        approved_amount: exactLineAmount.toFixed(2),
      });
    }
    }

    setApprovingId(id);
    setSubmittingApprove(true);
    try {
      const payload = {
        hip_id: facilityName,
        refund_request_id: id,
        status: "APPROVED",
        approved_amount: approvedTotal.toFixed(2),
        items: linePayload,
      };

      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}refund_approval/`,
        payload,
        { headers: getRefundApiHeaders() },
      );
      if (res?.data?.status === "Success") {
        toast.success(res?.data?.message || "Refund request approved.");
        resetApproveModal();
        fetchRefundList();
      } else {
        toast.error(res?.data?.message || "Approve failed.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Approve failed.",
      );
    } finally {
      setApprovingId(null);
      setSubmittingApprove(false);
    }
  };

  const fetchAllInvoicesForRefund = async ({ query = "" } = {}) => {
    if (!authToken || refundLoading) return;
    const trimmedQuery = String(query || "").trim();
    if (!trimmedQuery) return;
    setRefundLoading(true);
    try {
      // Search API pagination fetch
      let page = 1;
      const limit = 10;
      let hasNext = true;
      const aggregatedInvoices = [];
      const seenInvoiceIds = new Set();
      let safetyCounter = 0;

      while (hasNext && safetyCounter < 100) {
        const res = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}search_invoice/`,
          {
            hip_id: facilityName,
            invoice_number: trimmedQuery,
            patient_id: trimmedQuery,
            patient_name: trimmedQuery,
            page,
            limit,
          },
          { headers: getRefundApiHeaders() },
        );

        const pageInvoices = Array.isArray(res?.data?.invoices) ? res.data.invoices : [];
        pageInvoices.forEach((invoice) => {
          const key = String(invoice?.invoice_id ?? `${invoice?.invoice_number}-${invoice?.invoice_date}`);
          if (!seenInvoiceIds.has(key)) {
            seenInvoiceIds.add(key);
            aggregatedInvoices.push(invoice);
          }
        });

        const pg = res?.data?.pagination || {};
        const serverHasNext = Boolean(pg?.has_next);
        if (serverHasNext) {
          page = Number(pg?.next_page) || page + 1;
          hasNext = true;
        } else {
          // Fallback when pagination metadata is absent/inconsistent.
          hasNext = pageInvoices.length === limit;
          page += 1;
        }

        safetyCounter += 1;
      }

      setAllInvoicesForRefund(aggregatedInvoices);
    } catch (error) {
      console.error("Error fetching all invoices for refund:", error);
      toast.error("Unable to load invoices for refund.");
    } finally {
      setRefundLoading(false);
    }
  };

  const handleOpenRefundModal = async () => {
    setOpenRefundModal(true);
    setAllInvoicesForRefund([]);
  };

  const handleCloseRefundModal = () => {
    setOpenRefundModal(false);
    setRefundSearchQuery("");
    setDebouncedRefundSearchQuery("");
    setSelectedRefundInvoiceId("");
    setRefundRows([{ itemIndex: "", qty: "", amount: "" }]);
    setAmountRefundAmount("");
    setRefundNote("");
  };

  const normalizedRefundSearch = debouncedRefundSearchQuery.trim().toLowerCase();
  useEffect(() => {
    if (!openRefundModal || !authToken || !facilityName) return;
    if (!normalizedRefundSearch) {
      setAllInvoicesForRefund([]);
      return;
    }
    fetchAllInvoicesForRefund({ query: normalizedRefundSearch });
  }, [openRefundModal, normalizedRefundSearch, authToken, facilityName]);

  const shouldShowInvoiceSearchResults = normalizedRefundSearch.length > 0;
  const filteredRefundInvoices = allInvoicesForRefund.filter((invoice) => {
    if (!normalizedRefundSearch) return false;
    const patientName = displayPatientName(invoice?.patient_name || "").toLowerCase();
    const patientId = String(invoice?.patient_id ?? "").toLowerCase();
    const invoiceNo = String(invoice?.invoice_number ?? "").toLowerCase();
    return (
      patientName.includes(normalizedRefundSearch) ||
      patientId.includes(normalizedRefundSearch) ||
      invoiceNo.includes(normalizedRefundSearch)
    );
  });

  const selectedRefundInvoice = allInvoicesForRefund.find(
    (invoice) => String(invoice?.invoice_id) === String(selectedRefundInvoiceId),
  );
  const selectedInvoiceItems = getInvoiceItems(selectedRefundInvoice);
  const invoiceHasRefundableItems = (invoice) =>
    getInvoiceItems(invoice).some((item) => isItemRefundable(item));

  const isInvoicePartiallyPaid = (invoice) => {
    const status = String(invoice?.payment_status || "").toLowerCase();
    if (status === "paid") return false;
    const partial = Number(invoice?.partial_payment || 0);
    const total = Number(invoice?.total_amount || 0);
    return partial > 0 && partial < total;
  };

  const invoiceHasBillDiscount = (invoice) => Number(invoice?.discount || 0) > 0;

  const invoiceAmountCap = (invoice) => {
    const fromApi = Number(invoice?.amount_refund_cap);
    if (Number.isFinite(fromApi)) return Math.max(0, fromApi);
    const paid = Number(invoice?.partial_payment || 0);
    return Number.isFinite(paid) ? Math.max(0, paid) : 0;
  };

  const isAmountRefundRow = (row) => {
    if (String(row?.refund_mode || "").toUpperCase() === "AMOUNT") return true;
    const items = Array.isArray(row?.items) ? row.items : [];
    return items.length > 0 && items.every((it) => it?.item_id == null);
  };

  const handleSelectRefundInvoice = (invoiceId) => {
    setSelectedRefundInvoiceId(invoiceId);
    setRefundRows([{ itemIndex: "", qty: "", amount: "" }]);
    setAmountRefundAmount("");
  };

  const handleSelectRefundItem = (rowIndex, itemIndex) => {
    const item = selectedInvoiceItems[Number(itemIndex)];
    setRefundRows((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) return row;
        if (!item) return { ...row, itemIndex };
        const refundableQty = getRefundAvailableQty(item);
        const unitPrice = getItemUnitPrice(item);
        return {
          ...row,
          itemIndex,
          qty: refundableQty > 0 ? "1" : "",
          amount: refundableQty > 0 && unitPrice > 0 ? unitPrice.toFixed(2) : "",
        };
      }),
    );
  };

  const handleRefundRowChange = (rowIndex, fieldName, value) => {
    setRefundRows((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) return row;

        const selectedItem =
          row.itemIndex === "" ? null : selectedInvoiceItems[Number(row.itemIndex)];
        const maxRefundQty = selectedItem ? getRefundAvailableQty(selectedItem) : 0;
        const unitPrice = selectedItem ? getItemUnitPrice(selectedItem) : 0;

        if (fieldName === "qty") {
          const numericQty = Number(value);
          if (value === "") return { ...row, qty: "" };
          if (!Number.isFinite(numericQty)) return row;
          const clampedQty = Math.min(
            Math.max(Math.round(numericQty), 0),
            maxRefundQty || numericQty,
          );
          const nextAmount =
            unitPrice > 0 && clampedQty > 0 ? (unitPrice * clampedQty).toFixed(2) : "";
          return { ...row, qty: String(clampedQty), amount: nextAmount };
        }

        return { ...row, [fieldName]: value };
      }),
    );
  };

  const handleAddRefundRow = () => {
    setRefundRows((prevRows) => [...prevRows, { itemIndex: "", qty: "", amount: "" }]);
  };

  const handleRemoveRefundRow = (rowIndex) => {
    setRefundRows((prevRows) => {
      if (prevRows.length <= 1) return prevRows;
      return prevRows.filter((_, index) => index !== rowIndex);
    });
  };

  const isRefundRowValid = (row) => {
    if (row.itemIndex === "") return false;
    const selectedItem = selectedInvoiceItems[Number(row.itemIndex)];
    if (!selectedItem) return false;

    const refundAvailableQty = getRefundAvailableQty(selectedItem);
    const unitPrice = getItemUnitPrice(selectedItem);
    const qty = Number(row.qty);
    const amount = Number(row.amount);

    if (!row.qty || !Number.isFinite(qty) || qty <= 0) return false;
    if (qty > refundAvailableQty) return false;

    if (!row.amount || !Number.isFinite(amount) || amount <= 0) return false;
    const expectedAmount = unitPrice * qty;
    if (Math.abs(expectedAmount - amount) > 0.01) return false;

    return true;
  };

  const isAnyRowInvalid = refundRows.some((row) => !isRefundRowValid(row));

  const handleRequestRefundApproval = async () => {
    if (!selectedRefundInvoice) {
      toast.error("Please select an invoice for refund.");
      return;
    }
    const amountOnlyRefund = invoiceHasBillDiscount(selectedRefundInvoice);
    if (!amountOnlyRefund && isAnyRowInvalid) {
      toast.error("Please enter valid qty.");
      return;
    }
    const requestedBy = getRequestedByUserId();
    if (requestedBy == null) {
      toast.error(
        "Could not read user id from access token (JWT user_id). Please re-login and try again.",
      );
      return;
    }
    if (!REACT_APP_BACKEND_ROUTE_URL) {
      toast.error("Backend URL is not configured.");
      return;
    }

    const patientNameRaw =
      displayPatientName(selectedRefundInvoice?.patient_name || "") ||
      String(selectedRefundInvoice?.patient_name || "").trim();
    if (!patientNameRaw) {
      toast.error("Patient name is required for refund request.");
      return;
    }

    const patientIdRaw = selectedRefundInvoice?.patient_id ?? selectedRefundInvoice?.patientId;
    const invoicePk = Number(selectedRefundInvoice?.invoice_id);
    let body;

    if (amountOnlyRefund) {
      const cap = invoiceAmountCap(selectedRefundInvoice);
      const amt = Number(amountRefundAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        toast.error("Enter a refund amount.");
        return;
      }
      if (amt > cap + 0.001) {
        toast.error(`Refund cannot exceed the amount already paid (Rs ${cap.toFixed(2)}).`);
        return;
      }
      body = {
        refund_mode: "AMOUNT",
        invoice_no: String(selectedRefundInvoice?.invoice_number || "").trim(),
        patient_name: patientNameRaw,
        requested_amount: amt.toFixed(2),
        requested_by: requestedBy,
        reason: (refundNote || "").trim() || "Refund request",
        items: [],
        total_items: 0,
      };
    } else {
    const aggregatedMap = new Map();

    for (const row of refundRows) {
      const selectedItem = selectedInvoiceItems[Number(row.itemIndex)];
      if (!selectedItem) continue;
      const itemId = getInvoiceItemId(selectedItem);
      if (itemId == null) {
        toast.error(
          `invoice_item_id (numeric) missing for ${getItemName(selectedItem)}. Please contact backend.`,
        );
        return;
      }
      const qtyInt = Math.round(Number(row.qty));
      const availableQty = getRefundAvailableQty(selectedItem);
      if (qtyInt <= 0) continue;
      if (qtyInt > availableQty) {
        toast.error(
          `Only ${availableQty} qty available for ${getItemName(selectedItem)}, but you requested ${qtyInt}.`,
        );
        return;
      }
      const unitPrice = Number(getItemUnitPrice(selectedItem).toFixed(2));
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
        toast.error(`Invalid price for ${getItemName(selectedItem)}.`);
        return;
      }
      const mapKey = String(itemId);
      if (aggregatedMap.has(mapKey)) {
        const existing = aggregatedMap.get(mapKey);
        existing.quantity += qtyInt;
      } else {
        aggregatedMap.set(mapKey, {
          item_id: itemId,
          item_name: getItemName(selectedItem),
          quantity: qtyInt,
          price: unitPrice.toFixed(2),
          status: "PENDING",
          max_available_qty: availableQty,
        });
      }
    }

    const itemsPayload = [];
    let sumLines = 0;
    for (const entry of aggregatedMap.values()) {
      if (entry.quantity > entry.max_available_qty) {
        toast.error(
          `Only ${entry.max_available_qty} qty available for ${entry.item_name}, but you requested ${entry.quantity}.`,
        );
        return;
      }
      const line = Number((Number(entry.quantity) * Number(entry.price)).toFixed(2));
      sumLines = Number((sumLines + line).toFixed(2));
      const { max_available_qty, ...apiEntry } = entry;
      itemsPayload.push(apiEntry);
    }

    if (sumLines <= 0) {
      toast.error("Requested amount must be greater than zero.");
      return;
    }

    if (itemsPayload.length === 0) {
      toast.error("Add at least one valid refund line.");
      return;
    }

    const requestedAmountStr = sumLines.toFixed(2);

    body = {
      refund_mode: "ITEM",
      invoice_no: String(selectedRefundInvoice?.invoice_number || "").trim(),
      patient_name: patientNameRaw,
      requested_amount: requestedAmountStr,
      requested_by: requestedBy,
      reason: (refundNote || "").trim() || "Refund request",
      items: itemsPayload,
      total_items: itemsPayload.length,
    };
    }
    if (Number.isFinite(invoicePk) && invoicePk > 0) {
      body.invoice_id = invoicePk;
    }
    if (patientIdRaw != null && String(patientIdRaw).trim() !== "") {
      const pid = Number(patientIdRaw);
      body.patient_id = Number.isFinite(pid) ? pid : String(patientIdRaw).trim();
    }

    if (!body.invoice_no) {
      toast.error("Invoice number is missing.");
      return;
    }

    setSubmittingRefund(true);
    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}request_refund_approval/`,
        body,
        { headers: getRefundApiHeaders() },
      );
      if (res?.data?.status === "Success") {
        toast.success(res?.data?.message || "Refund request submitted successfully.");
        const cancelledIds = res?.data?.data?.cancelled_appointment_ids;
        if (Array.isArray(cancelledIds) && cancelledIds.length > 0) {
          toast.info(
            `Linked appointment(s) cancelled: ${cancelledIds.join(", ")}`,
          );
        }
        handleCloseRefundModal();
        setAllInvoicesForRefund([]);
        fetchRefundList();
      } else {
        const msg = res?.data?.message || "Refund request failed.";
        toast.error(msg);
        const exp = res?.data?.data?.expected_from_items;
        if (exp) {
          toast.info(`Expected from items: ${exp}`);
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.data;
      if (
        detail &&
        detail.item_id != null &&
        detail.available_qty != null &&
        detail.requested_qty != null
      ) {
        toast.error(
          `Only ${detail.available_qty} qty available for Item ${detail.item_id}, but you requested ${detail.requested_qty}.`,
        );
      }
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Refund request failed.";
      toast.error(msg);
    } finally {
      setSubmittingRefund(false);
    }
  };

  const isUserTypingSearch =
    refundSearchQuery.trim().length > 0 &&
    refundSearchQuery.trim() !== debouncedRefundSearchQuery.trim();

  const getNoInvoiceMessage = () => {
    if (refundLoading) return "Loading invoices...";
    if (!shouldShowInvoiceSearchResults)
      return "Type to search invoice by Patient ID / Invoice No / Patient Name.";
    if (isUserTypingSearch) return "Searching invoices...";
    return "No invoice found for this search.";
  };

  return (
    <>
      <SideNavbar />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          padding: "12px",
          gap: "20px",
          marginBottom: "5px",
        }}
      >
        <p
          style={{
            fontSize: "32px",
            fontWeight: "bold",
            textAlign: "left",
            color: "#172935",
            margin: 0,
          }}
        >
          Refund
        </p>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by Name"
            value={refundSearchText}
            onChange={(e) => setRefundSearchText(e.target.value)}
            className="billing-search-field"
            sx={{
              minWidth: 260,
              "& .MuiOutlinedInput-root": {
                height: "42px",
                borderRadius: "10px",
              },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <button
            onClick={handleOpenRefundModal}
            type="button"
            className="btn btn__secondary btn__rounded btn__block mt-2"
            style={{
              backgroundColor: "#0E3F66",
              borderRadius: "8px",
              fontSize: "14px",
              textTransform: "none",
              color: "#fff",
            }}
          >
            Initialize Refund
          </button>
        </Box>
      </Box>

      <div className="display-flex">
        {permissionLoading ? null : refundListLoading && refundList.length === 0 ? (
          <Box sx={{ p: 4, width: "100%", textAlign: "center" }}>
            <Typography color="text.secondary">Loading refunds…</Typography>
          </Box>
        ) : refundList.length > 0 ? (
          <DataGrid
            rows={refundList}
            getRowId={(row) => row.refund_request_id ?? row.id}
            columns={[
              {
                field: "invoice_number",
                headerName: "Invoice Number",
                width: 200,
              },
              {
                field: "refund_number",
                headerName: "Refund Number",
                width: 170,
                renderCell: (params) => <span>{params.value || "—"}</span>,
              },
              {
                field: "patient_name",
                headerName: "Name",
                width: 200,
                renderCell: (params) => (
                  <span>{displayPatientName(params.value)}</span>
                ),
              },
              {
                field: "created_at",
                headerName: "Request Date",
                width: 140,
                sortable: true,
                renderCell: (params) => (
                  <DisplayDate value={params.row?.created_at} emptyLabel="—" />
                ),
                valueGetter: (a, b) => {
                  const row =
                    a && typeof a === "object" && "row" in a ? a.row : b;
                  return formatDisplayDate(row?.created_at);
                },
              },
              {
                field: "requested_amount",
                headerName: "Refund Request Amount",
                width: 130,
                align: "right",
                headerAlign: "right",
                renderCell: (params) => (
                  <span>{formatInr(params.value)}</span>
                ),
              },
              {
                field: "approved_amount",
                headerName: "Approved Amount",
                width: 130,
                align: "right",
                headerAlign: "right",
                renderCell: (params) => {
                  const status = String(params.row?.status || "").toUpperCase();
                  if (status !== "APPROVED") return <span>—</span>;
                  return <span>{formatInr(params.value)}</span>;
                },
              },
              {
                field: "invoice_date",
                headerName: "Invoice Date",
                width: 130,
                sortable: true,
                renderCell: (params) => (
                  <DisplayDate value={params.row?.invoice_date} emptyLabel="—" />
                ),
                valueGetter: (a, b) => {
                  const row =
                    a && typeof a === "object" && "row" in a ? a.row : b;
                  return formatDisplayDate(row?.invoice_date);
                },
              },
              {
                field: "status",
                headerName: "Status",
                width: 130,
                renderCell: (params) => {
                  const s = String(params.value || "").toUpperCase();
                  let color = "#172935";
                  if (s === "PENDING") color = "#9a3412";
                  else if (s === "APPROVED") color = "#065f46";
                  else if (s === "REJECTED") color = "#991b1b";
                  return (
                    <span style={{ color, fontWeight: 600 }}>{s || "—"}</span>
                  );
                },
              },
              {
                field: "settlement_status",
                headerName: "Settlement",
                width: 140,
                renderCell: (params) => {
                  const s = String(params.value || "UNSETTLED").toUpperCase();
                  const color = s === "SETTLED" ? "#065f46" : "#9a3412";
                  return <span style={{ color, fontWeight: 600 }}>{s}</span>;
                },
              },
              {
                field: "actions",
                headerName: "Actions",
                width: 300,
                sortable: false,
                renderCell: (params) => {
                  const status = String(params.row?.status || "").toUpperCase();
                  const settlementStatus = String(
                    params.row?.settlement_status || "UNSETTLED",
                  ).toUpperCase();
                  const rowBusy = isRefundRowActionBusy(params.row);
                  const canShowPayment =
                    !isFacilityAdmin &&
                    status === "APPROVED" &&
                    settlementStatus === "UNSETTLED";
                  return (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                      {isFacilityAdmin && (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={status !== "PENDING" || rowBusy}
                            onClick={() => openApproveModal(params.row)}
                            sx={{
                              backgroundColor: "#0E3F66",
                              color: "#fff",
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: "8px",
                              minWidth: 120,
                            }}
                          >
                            {submittingApprove && approvingId === params.row.refund_request_id
                              ? "…"
                              : "Approve Request"}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={status !== "PENDING" || rowBusy}
                            onClick={() => openRejectModal(params.row)}
                            sx={{
                              backgroundColor: "#7f1d1d",
                              color: "#fff",
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: "8px",
                              minWidth: 120,
                            }}
                          >
                            {submittingReject && rejectingId === params.row.refund_request_id
                              ? "…"
                              : "Reject Request"}
                          </Button>
                        </>
                      )}
                      {canShowPayment && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenRefundPayment(params.row)}
                          sx={{
                            backgroundColor: "#0E3F66",
                            color: "#fff",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                          }}
                        >
                          Payment
                        </Button>
                      )}
                      {!isFacilityAdmin && status === "APPROVED" && settlementStatus === "SETTLED" && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ color: "#065f46", fontWeight: 600, fontSize: 12 }}>
                            Settled
                          </Typography>
                          <IconButton
                            size="small"
                            title="View Refund Receipt"
                            onClick={() => handleOpenRefundReceipt(params.row)}
                          >
                            <VisibilityIcon sx={{ fontSize: 18, color: "#0E3F66" }} />
                          </IconButton>
                        </Box>
                      )}
                      {!isFacilityAdmin &&
                        (status === "PENDING" || status === "REJECTED") && (
                          <Typography sx={{ color: "#6b7280", fontWeight: 500, fontSize: 12 }}>
                            —
                          </Typography>
                        )}
                    </Box>
                  );
                },
              },
            ]}
            components={{
              Toolbar: () => (
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1, px: 1, pt: 1 }}>
                  <Grid item sx={{ display: "flex", gap: 1 }}>
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarDensitySelector />
                  </Grid>
                </Grid>
              ),
            }}
            paginationMode="server"
            rowCount={refundPagination.total_records}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            loading={refundListLoading}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#125386",
                color: "#fff",
                fontWeight: 600,
              },
            }}
          />
        ) : (
          <NoDataPage />
        )}
      </div>

      <InvoicePayment
        open={openPaymentModal}
        onClose={() => setOpenPaymentModal(false)}
        invoice={invoiceForPayment}
        allowPartialPayment={false}
        useRefundPaymentApi
        onAfterClose={() => setInvoiceForPayment(null)}
        onPaymentSuccess={() => {
          fetchRefundList();
        }}
      />

      <Dialog
        open={approveModalOpen}
        onClose={closeApproveModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "10px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#21417A",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            py: 1.75,
          }}
        >
          Approve Refund Request
          <IconButton onClick={closeApproveModal} sx={{ color: "#fff" }} disabled={submittingApprove}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Invoice Number
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={approveModalRow?.invoice_number ?? ""}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Patient Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={approveModalRow?.patient_name ?? ""}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Request Date
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formatDisplayDate(approveModalRow?.created_at)}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Refund Request Amount
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formatInr(approveModalRow?.requested_amount)}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
          </Grid>

          {approveLineState.length > 0 && isAmountRefundRow(approveModalRow) ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                Bill amount refund (invoice has a bill discount)
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: "10px", borderColor: "#e5e7eb" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151" }}>
                        Requested Amount
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151", minWidth: 160 }}>
                        Approved Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approveLineState.map((line) => (
                      <TableRow key={line.key}>
                        <TableCell>{line.item?.item_name ?? "Bill amount refund"}</TableCell>
                        <TableCell align="right">{formatInr(line.requestedAmount)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0.01, max: line.requestedAmount || 0, step: "0.01" }}
                            onWheel={(e) => e.target.blur()}
                            value={line.approvedAmount}
                            onChange={(e) => updateApproveAmount(line.key, e.target.value)}
                            error={Number(line.approvedAmount) > Number(line.requestedAmount || 0) + 0.001}
                            helperText={`Up to Rs ${formatInr(line.requestedAmount)}`}
                            sx={{
                              maxWidth: 160,
                              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography
                variant="body2"
                sx={{ mt: 1.5, textAlign: "right", color: "#374151", fontWeight: 600 }}
              >
                Total Approved Amount:{" "}
                {formatInr(
                  approveLineState.reduce(
                    (sum, line) => Number((sum + Number(line.approvedAmount || 0)).toFixed(2)),
                    0,
                  ),
                )}
              </Typography>
            </Box>
          ) : approveLineState.length > 0 ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                Items <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: "10px", borderColor: "#e5e7eb" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                      <TableCell padding="checkbox" sx={{ fontWeight: 600, color: "#374151" }} />
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Item Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151" }}>
                        Requested Qty
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151" }}>
                        Approved Qty
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151" }}>
                        Line Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151", minWidth: 140 }}>
                        Approved Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approveLineState.map((line) => (
                      <TableRow key={line.key}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={line.checked}
                            onChange={() => toggleApproveLine(line.key)}
                            sx={{ color: "#21417A", "&.Mui-checked": { color: "#21417A" } }}
                          />
                        </TableCell>
                        <TableCell>{line.item?.item_name ?? "—"}</TableCell>
                        <TableCell align="right">{line.requestedQuantity ?? "—"}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, max: line.requestedQuantity || 0, step: "1" }}
                            onWheel={(e) => e.target.blur()}
                            value={line.approvedQuantity}
                            onChange={(e) => updateApproveQuantity(line.key, e.target.value)}
                            disabled={!line.checked}
                            sx={{
                              maxWidth: 120,
                              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{formatInr(line.item?.subtotal)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={
                              line.checked
                                ? (line.unitPrice * Number(line.approvedQuantity || 0)).toFixed(2)
                                : "0.00"
                            }
                            InputProps={{ readOnly: true }}
                            sx={{
                              maxWidth: 140,
                              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography
                variant="body2"
                sx={{ mt: 1.5, textAlign: "right", color: "#374151", fontWeight: 600 }}
              >
                Total Approved Amount:{" "}
                {formatInr(
                  approveLineState.reduce((sum, line) => {
                    if (!line.checked) return sum;
                    return Number(
                      (
                        sum +
                        Number(line.unitPrice || 0) * Number(line.approvedQuantity || 0)
                      ).toFixed(2),
                    );
                  }, 0),
                )}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                No line items were returned for this request. Approval needs item-level data from API.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            onClick={closeApproveModal}
            disabled={submittingApprove}
            sx={{
              backgroundColor: "#21417A",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "10px",
              px: 4,
              py: 1.25,
              textTransform: "uppercase",
              minWidth: 140,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitApproveRefundFromModal}
            disabled={submittingApprove || approveLineState.length === 0}
            sx={{
              backgroundColor: "#21417A",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "10px",
              px: 4,
              py: 1.25,
              textTransform: "uppercase",
              minWidth: 140,
            }}
          >
            {submittingApprove ? "…" : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectModalOpen}
        onClose={closeRejectModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "10px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#21417A",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            py: 1.75,
          }}
        >
          Reject Refund Request
          <IconButton onClick={closeRejectModal} sx={{ color: "#fff" }} disabled={submittingReject}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Invoice Number
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={rejectModalRow?.invoice_number ?? ""}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Patient Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={rejectModalRow?.patient_name ?? ""}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Request Date
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formatDisplayDate(rejectModalRow?.created_at)}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Refund Request Amount
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formatInr(rejectModalRow?.requested_amount)}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                Request Reason
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                value={rejectModalRow?.reason ?? "—"}
                InputProps={{ readOnly: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f3f4f6",
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
              Items
            </Typography>
            {Array.isArray(rejectModalRow?.items) && rejectModalRow.items.length > 0 ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: "10px", borderColor: "#e5e7eb" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Item Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151" }}>
                        Qty
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151" }}>
                        Unit Price
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "#374151" }}>
                        Line Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rejectModalRow.items.map((it, idx) => (
                      <TableRow key={String(it?.id ?? idx)}>
                        <TableCell>{it?.item_name ?? "—"}</TableCell>
                        <TableCell align="right">{it?.quantity ?? "—"}</TableCell>
                        <TableCell align="right">{formatInr(it?.item_price)}</TableCell>
                        <TableCell align="right">{formatInr(it?.subtotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                No line items were returned for this request.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            onClick={closeRejectModal}
            disabled={submittingReject}
            sx={{
              backgroundColor: "#21417A",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "10px",
              px: 4,
              py: 1.25,
              textTransform: "uppercase",
              minWidth: 140,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitRejectRefundFromModal}
            disabled={submittingReject}
            sx={{
              backgroundColor: "#21417A",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "10px",
              px: 4,
              py: 1.25,
              textTransform: "uppercase",
              minWidth: 140,
            }}
          >
            {submittingReject ? "…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRefundModal}
        onClose={handleCloseRefundModal}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            width: "92vw",
            maxWidth: "1320px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#21417A",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
          }}
        >
          Request Refund
          <IconButton onClick={handleCloseRefundModal} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by Patient ID / Invoice No / Patient Name"
            value={refundSearchQuery}
            onChange={(e) => setRefundSearchQuery(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              maxHeight: "160px",
              overflowY: "auto",
              mb: 2,
            }}
          >
            {shouldShowInvoiceSearchResults && filteredRefundInvoices.length > 0 ? (
              filteredRefundInvoices.map((invoice) => {
                const selected = String(invoice?.invoice_id) === String(selectedRefundInvoiceId);
                const billDiscount = invoiceHasBillDiscount(invoice);
                const amountCap = billDiscount ? invoiceAmountCap(invoice) : 0;
                const paidSoFar = Number(invoice?.partial_payment || 0);
                // Bill-discount invoice: amount refund only, capped at what is still
                // refundable from the paid amount. Partial payment does not block here.
                const partiallyPaid = !billDiscount && isInvoicePartiallyPaid(invoice);
                const selectableInvoice = billDiscount
                  ? amountCap > 0
                  : invoiceHasRefundableItems(invoice) && !partiallyPaid;
                const rowLabel = selectableInvoice
                  ? billDiscount
                    ? `Amount refund only — up to Rs ${formatInr(amountCap)}`
                    : `Rs ${formatInr(invoice?.total_amount || 0)}`
                  : billDiscount
                    ? paidSoFar > 0
                      ? "Paid amount already under refund"
                      : "No payment to refund"
                    : partiallyPaid
                      ? "Partially paid — complete payment before refund"
                      : "No refundable items";
                return (
                  <Box
                    key={invoice?.invoice_id}
                    onClick={() =>
                      selectableInvoice && handleSelectRefundInvoice(String(invoice?.invoice_id))
                    }
                    sx={{
                      cursor: selectableInvoice ? "pointer" : "not-allowed",
                      px: 2,
                      py: 1.5,
                      borderBottom: "1px solid #f1f5f9",
                      backgroundColor: selected ? "#edf2ff" : "#fff",
                      opacity: selectableInvoice ? 1 : 0.5,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography fontWeight={600}>
                      {invoice?.invoice_number || "--"}{" "}
                      <span style={{ fontWeight: 400, marginLeft: 8 }}>
                        {displayPatientName(invoice?.patient_name) || "--"}
                      </span>
                    </Typography>
                    <Typography
                      fontWeight={600}
                      sx={partiallyPaid || (billDiscount && selectableInvoice) ? { color: "#b45309" } : undefined}
                    >
                      {rowLabel}
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Typography sx={{ p: 2 }} color="text.secondary">
                {getNoInvoiceMessage()}
              </Typography>
            )}
          </Box>

          {selectedRefundInvoice && invoiceHasBillDiscount(selectedRefundInvoice) ? (
            <Card sx={{ p: 2, mb: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                Refund Amount
              </Typography>
              <Typography variant="body2" sx={{ color: "#b45309", mb: 2 }}>
                This invoice has a bill discount. Item-wise refund is not available; enter one
                amount up to the paid amount.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  {(() => {
                    const cap = invoiceAmountCap(selectedRefundInvoice);
                    const amt = Number(amountRefundAmount);
                    const overCap = amountRefundAmount !== "" && amt > cap + 0.001;
                    return (
                      <TextField
                        fullWidth
                        label="Refund Amount (₹)"
                        type="number"
                        value={amountRefundAmount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "" || Number(v) >= 0) setAmountRefundAmount(v);
                        }}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{ min: 0.01, max: cap, step: "0.01" }}
                        error={overCap}
                        helperText={
                          overCap
                            ? `Cannot exceed Rs ${formatInr(cap)}`
                            : `Up to Rs ${formatInr(cap)} (paid so far)`
                        }
                        sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}
                      />
                    );
                  })()}
                </Grid>
              </Grid>
            </Card>
          ) : (
          <Card sx={{ p: 2, mb: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
            <Typography fontWeight={600} sx={{ mb: 2 }}>
              Items
            </Typography>
            {refundRows.map((row, rowIndex) => (
              <Grid
                container
                spacing={2}
                key={`refund-row-${rowIndex}`}
                sx={{ mb: rowIndex === refundRows.length - 1 ? 0 : 1 }}
              >
                <Grid item xs={12} md={6} lg={3}>
                  <TextField
                    select
                    fullWidth
                    label="Invoice Item"
                    value={row.itemIndex}
                    onChange={(e) => handleSelectRefundItem(rowIndex, e.target.value)}
                    disabled={!selectedRefundInvoice}
                    sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}
                  >
                    {selectedInvoiceItems.map((item, index) => {
                      return (
                        <MenuItem
                          key={`${getItemName(item)}-${index}`}
                          value={String(index)}
                          disabled={!isItemRefundable(item)}
                        >
                          {getItemName(item)}
                          {item?.is_doctor_appointment_charge && item?.appointment_id
                            ? ` (Appointment ${item.appointment_id})`
                            : ""}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                  {(() => {
                    const selectedItem =
                      row.itemIndex === ""
                        ? null
                        : selectedInvoiceItems[Number(row.itemIndex)];
                    if (
                      !selectedItem?.is_doctor_appointment_charge ||
                      !selectedItem?.appointment_id
                    ) {
                      return null;
                    }
                    return (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5, color: "#b45309" }}
                      >
                        Raising this refund will cancel appointment{" "}
                        {selectedItem.appointment_id}.
                      </Typography>
                    );
                  })()}
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={1.75}>
                  {(() => {
                    const selectedItem =
                      row.itemIndex === ""
                        ? null
                        : selectedInvoiceItems[Number(row.itemIndex)];
                    const isDoctorConsultation = selectedItem
                      ? getItemName(selectedItem).toLowerCase().trim() === "doctor consultation"
                      : false;
                    const listPrice = selectedItem
                      ? (isDoctorConsultation ? getItemUnitPrice(selectedItem) : getItemListPrice(selectedItem))
                      : 0;
                    return (
                      <TextField
                        fullWidth
                        label="Item Price (₹)"
                        type="number"
                        value={listPrice > 0 ? listPrice.toFixed(2) : ""}
                        InputProps={{ readOnly: true }}
                        sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={1.25}>
                  {(() => {
                    const selectedItem =
                      row.itemIndex === ""
                        ? null
                        : selectedInvoiceItems[Number(row.itemIndex)];
                    const discount = selectedItem ? getItemDiscountPercent(selectedItem) : 0;
                    return (
                      <TextField
                        fullWidth
                        label="Discount %"
                        type="number"
                        value={selectedItem ? discount : ""}
                        InputProps={{ readOnly: true }}
                        sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={1.75}>
                  {(() => {
                    const selectedItem =
                      row.itemIndex === ""
                        ? null
                        : selectedInvoiceItems[Number(row.itemIndex)];
                    const chargedPrice = selectedItem ? getItemUnitPrice(selectedItem) : 0;
                    return (
                      <TextField
                        fullWidth
                        label="Charged Price (₹)"
                        type="number"
                        value={chargedPrice > 0 ? chargedPrice.toFixed(2) : ""}
                        InputProps={{ readOnly: true }}
                        sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={1.5}>
                  {(() => {
                    const selectedItem =
                      row.itemIndex === ""
                        ? null
                        : selectedInvoiceItems[Number(row.itemIndex)];
                    const availableQty = selectedItem
                      ? getRefundAvailableQty(selectedItem)
                      : undefined;
                    return (
                  <TextField
                    fullWidth
                    label="Requested Qty"
                    type="number"
                    value={row.qty}
                    onChange={(e) =>
                      handleRefundRowChange(rowIndex, "qty", e.target.value)
                    }
                    onWheel={(e) => e.target.blur()}
                    inputProps={{ min: 1, max: availableQty || undefined, step: "1" }}
                    helperText={
                      selectedItem
                        ? `Available Qty: ${availableQty}`
                        : "Select item first"
                    }
                    sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}
                  />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={1.75}>
                  {(() => {
                    const selectedItem =
                      row.itemIndex === ""
                        ? null
                        : selectedInvoiceItems[Number(row.itemIndex)];
                    const unitPrice = selectedItem ? getItemUnitPrice(selectedItem) : 0;
                    const qty = Number(row.qty || 0);
                    const maxAmount = unitPrice > 0 && qty > 0 ? unitPrice * qty : undefined;
                    return (
                  <TextField
                    fullWidth
                    label="Refund Rs (charged)"
                    type="number"
                    value={row.amount}
                    InputProps={{ readOnly: true }}
                    inputProps={{ min: 0.01, max: maxAmount, step: "0.01" }}
                    sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}
                  />
                    );
                  })()}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  lg={1}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <IconButton
                    onClick={handleAddRefundRow}
                    color="primary"
                    size="small"
                    title="Add item row"
                    disabled={!selectedRefundInvoice}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleRemoveRefundRow(rowIndex)}
                    color="error"
                    size="small"
                    title="Remove item row"
                    disabled={refundRows.length === 1}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Card>
          )}

          <Card sx={{ p: 2, boxShadow: "none", border: "1px solid #e5e7eb" }}>
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              Note
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Enter note..."
              value={refundNote}
              onChange={(e) => setRefundNote(e.target.value)}
            />
          </Card>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
          <Button onClick={handleCloseRefundModal} sx={{ minWidth: 110, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRequestRefundApproval}
            disabled={submittingRefund}
            sx={{
              minWidth: 180,
              textTransform: "none",
              backgroundColor: "#21417A",
              "&:hover": { backgroundColor: "#1b3666" },
            }}
          >
            {submittingRefund ? "Submitting…" : "Request Approval"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Refund;
